import React from 'react';
import PropTypes from 'prop-types';
import set from 'lodash/set';
import get from 'lodash/get';

import ColorPickerEditor from 'common/components/ColorPicker';
import FontPickerEditor from 'common/components/FontPicker/FontPickerEditor';

import { useNode } from '@splashthat/splash-cms-engine';

const FIRST_COLOR_LABEL = 'Default';
const SECOND_COLOR_LABEL = 'Hover';
const TRANSPARENT_FALLBACK = { value: 'transparent' };
export const DEFAULT_FONT_SIZE_LIST = [14, 16, 18, 20];
export const HEADLINE_FONT_SIZE_LIST = [24, 32, 40, 48, 56, 64];

export default function FontTools({
    colors,
    activeFonts,
    fontSizeList,
    familyPath,
    colorPath,
    colorHoverPath,
    sizePath,
    fontAriaLabel,
    colorAriaLabel,
    qaId,
    hasHover,
}) {
    const node = useNode();
    const colorList = colors.map((color, index) => ({
        id: index,
        value: color,
    }));
    const fontList = activeFonts.map((color, index) => ({
        id: index,
        value: color,
    }));

    const onUpdateDefaultColor = (id) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, colorPath, id);
            return editedNode;
        });

    const onUpdateFontColorHover = (id) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, colorHoverPath, id);
            return editedNode;
        });

    const onUpdateFontFamily = (id) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, familyPath, id);
            return editedNode;
        });

    const onUpdateFontSize = (size) =>
        node.actions.setProp((editedNode) => {
            set(editedNode, sizePath, size);
            return editedNode;
        });

    const hoverProps = hasHover
        ? {
              // We don't include a label on the first color if there is no second color
              firstColorLabel: FIRST_COLOR_LABEL,
              secondColor: colorList[get(node, colorHoverPath)] || TRANSPARENT_FALLBACK,
              changeSecondColor: onUpdateFontColorHover,
              secondColorLabel: SECOND_COLOR_LABEL,
          }
        : {};

    return (
        <React.Fragment>
            <FontPickerEditor
                fontFamilyList={fontList}
                fontSizeList={fontSizeList}
                defaultFontFamily={fontList[get(node, familyPath)]}
                onChangeFontFamily={onUpdateFontFamily}
                defaultFontSize={get(node, sizePath)}
                activeFontSize={get(node, sizePath)}
                onChangeFontSize={onUpdateFontSize}
                ariaLabel={fontAriaLabel}
                qaId={`${qaId || node.id}-font-family-size-picker`}
            />
            <ColorPickerEditor
                colorList={colorList}
                firstColor={colorList[get(node, colorPath)] || TRANSPARENT_FALLBACK}
                changeFirstColor={onUpdateDefaultColor}
                aria-label={colorAriaLabel}
                qaId={`${qaId || node.id}-font-color-picker`}
                {...hoverProps}
            />
        </React.Fragment>
    );
}

FontTools.propTypes = {
    colors: PropTypes.array.isRequired,
    activeFonts: PropTypes.array.isRequired,
    fontSizeList: PropTypes.array,
    familyPath: PropTypes.string,
    colorPath: PropTypes.string,
    colorHoverPath: PropTypes.string,
    sizePath: PropTypes.string,
    fontAriaLabel: PropTypes.string,
    colorAriaLabel: PropTypes.string,
    qaId: PropTypes.string,
    hasHover: PropTypes.bool,
};

FontTools.defaultProps = {
    fontSizeList: DEFAULT_FONT_SIZE_LIST,
    familyPath: 'styles.fontFamily',
    colorPath: 'styles.fontColor',
    colorHoverPath: 'styles.fontColorHover',
    sizePath: 'styles.fontSize',
    fontAriaLabel: '',
    colorAriaLabel: '',
    qaId: '',
    hasHover: false,
};
